import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import handshakeIcon from "src/assets/svg-icons/aggregators/handshake.svg";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useNavigate, useParams } from "react-router-dom";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  useAddAggMutation,
  useGetAggregatorById,
  useUpdateAggMutation,
} from "src/api/aggreagators";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";

const methodsOptions = [
  {
    label: "delivery",
    value: "delivery",
  },
  {
    label: "pickup",
    value: "pickup",
  },
];

const schema = yup.object().shape({
  name: yup.string().required("Required"),
  payment_methods: yup
    .array()
    .of(yup.number().required("Required"))
    .required("Required")
    .min(1, "Required"),
  aggFeesList: yup
    .array()
    .of(
      yup.object().shape({
        from: yup.number().typeError("required").required("required"),
        to: yup.number().typeError("required").required("required"),
        fee: yup.number().typeError("required").required("required"),
        feeType: yup
          .string()
          .required("required")
          .oneOf(["percentage", "amount"] as const),
        applyOn: yup
          .array()
          .of(yup.string().required())
          .required()
          .min(1, "Required"),
        payment_methods: yup
          .array()
          .of(yup.number().required("Required"))
          .required("Required")
          .min(1, "Required"),
        branches: yup
          .array()
          .of(yup.number().required("Required"))
          .required("Required")
          .min(1, "Required"),
      }),
    )
    .required("Required")
    .min(1, "At least one Aggregator commission is required"),
  deliveryCostList: yup
    .array()
    .of(
      yup.object().shape({
        from: yup.number().typeError("required").required("required"),
        to: yup.number().typeError("required").required("required"),
        fee: yup.number().typeError("required").required("required"),
        feeType: yup
          .string()
          .required("")
          .oneOf(["percentage", "amount"] as const),
        applyOn: yup
          .array()
          .of(yup.string().required())
          .required()
          .min(1, "Required"),
        payment_methods: yup
          .array()
          .of(yup.number().required("Required"))
          .required("Required")
          .min(1, "Required"),
        branches: yup
          .array()
          .of(yup.number().required("Required"))
          .required("Required")
          .min(1, "Required"),
      }),
    )
    .required("Required"),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const AddNewAggregator = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation("aggregators");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode: "add" | "edit" = params?.id ? "edit" : "add";

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const { fields, append, remove, update } = useFieldArray({
    name: "aggFeesList",
    control,
  });

  const {
    fields: deliveryFields,
    append: appendDeliveryCost,
    remove: removeDeliveryCost,
  } = useFieldArray({
    name: "deliveryCostList",
    control,
  });

  // APIS
  const { data: paymentsData } = useGetPaymentMethods({
    enabled: true,
  });
  const { data: branchesData } = useGetBranches();
  const { mutate, status, error, isPending } = useAddAggMutation();
  const {
    mutate: mutateUpdate,
    error: errorUpdate,
    status: statusUpdate,
    isPending: isPendingUpdate,
  } = useUpdateAggMutation();
  const { data, status: getAggByIdStatus } = useGetAggregatorById({
    enabled: !!params.id,
    id: params.id as string,
  });

  const handleAppendFee = () => {
    append({
      from: 0,
      to: 0,
      fee: 0,
      feeType: "percentage",
      applyOn: [],
      payment_methods: [],
      branches: [],
    });
  };

  const handleAppendDeliveryCost = () => {
    appendDeliveryCost({
      from: 0,
      to: 0,
      fee: 0,
      feeType: "percentage",
      applyOn: [],
      payment_methods: [],
      branches: [],
    });
  };

  const handleRemoveFee = (index: number) => {
    remove(index);
  };

  const handleRemoveDeliveryCost = (index: number) => {
    removeDeliveryCost(index);
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { name, payment_methods, aggFeesList, deliveryCostList } = data;
    const aggFeesAndDeliveryCosts = [
      ...aggFeesList.map((aggFee) => ({
        from_amount: aggFee.from,
        to_amount: aggFee.to,
        fee_percentage: aggFee.fee,
        delivery_methods: aggFee.applyOn,
        payment_methods: aggFee.payment_methods,
        branches: aggFee.branches,
        type: "aggregator_fee",
        is_percentage: aggFee.feeType === "percentage" ? 1 : 0,
      })),
      ...deliveryCostList.map((cost) => ({
        from_amount: cost.from,
        to_amount: cost.to,
        fee_percentage: cost.fee,
        delivery_methods: cost.applyOn,
        payment_methods: cost.payment_methods,
        branches: cost.branches,
        type: "delivery_cost",
        is_percentage: cost.feeType === "percentage" ? 1 : 0,
      })),
    ];

    const dataToSend = {
      name: data.name,
      payment_methods: data.payment_methods,
      ...(aggFeesAndDeliveryCosts.length > 0
        ? { aggregator_fee_and_delivery_costs: aggFeesAndDeliveryCosts }
        : {}),
    };
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      mutateUpdate({
        id: params.id,
        ...dataToSend,
      });
    }
  };

  const navigateToAggregators = () => {
    navigate("/aggregators/list");
  };

  useEffect(() => {
    const selectedPayments = watch("payment_methods") ?? [];

    const aggFees = getValues("aggFeesList") ?? [];
    const deliveryCosts = getValues("deliveryCostList") ?? [];

    aggFees.forEach((fee, index) => {
      const filtered = fee.payment_methods.filter((pm) =>
        selectedPayments.includes(pm),
      );

      if (filtered.length !== fee.payment_methods.length) {
        setValue(`aggFeesList.${index}.payment_methods`, filtered, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });

    deliveryCosts.forEach((cost, index) => {
      const filtered = cost.payment_methods.filter((pm) =>
        selectedPayments.includes(pm),
      );

      if (filtered.length !== cost.payment_methods.length) {
        setValue(`deliveryCostList.${index}.payment_methods`, filtered, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
  }, [watch("payment_methods")]);

  //Get agg by Id success and error handling
  useEffect(() => {
    if (getAggByIdStatus === "success") {
      console.log({ data });
      reset({
        name: data.data.name,
        payment_methods: data.data.related_payment_methods.map(
          (payment: any) => payment.payment_method?.id,
        ),
        aggFeesList: data.data.aggregator_fees.map((fee: any) => {
          return {
            from: fee.from_amount,
            to: fee.to_amount,
            fee: fee.fee_percentage,
            applyOn: fee.delivery_methods,
            payment_methods: fee.payment_methods,
            branches: fee.branches,
            feeType: fee.is_percentage ? "percentage" : "amount",
          };
        }),
        deliveryCostList: data.data.aggregator_delivery_costs.map(
          (cost: any) => {
            return {
              from: cost.from_amount,
              to: cost.to_amount,
              fee: cost.fee_percentage,
              applyOn: cost.delivery_methods,
              payment_methods: cost.payment_methods,
              branches: cost.branches,
              feeType: cost.is_percentage ? "percentage" : "amount",
            };
          },
        ),
      });
    }
  }, [getAggByIdStatus]);

  //Add agg success and error handling
  useEffect(() => {
    if (status === "success") {
      navigateToAggregators();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update agg success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: errorUpdate?.data.errors?.[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  return (
    <Stack height={"100%"}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: "100%" }}>
        <Stack height={"100%"} justifyContent={"space-between"}>
          <Box p={{ xs: 2, sm: 4 }}>
            <Stack direction={"row"} spacing={"12px"} mb={3}>
              <Box
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                width={"60px"}
                height={"60px"}
                bgcolor={"#D0D5DD"}
                borderRadius={"16px"}
              >
                <img src={handshakeIcon} width={"32px"} alt="" />
              </Box>
              <Box>
                <Typography fontSize={"32px"} lineHeight={"38.4px"}>
                  {t("aggs")}
                </Typography>
                {mode === "edit" && (
                  <Typography
                    fontSize={"20px"}
                    lineHeight={"24px"}
                    color={"#98A2B3"}
                  >
                    {t("lastUpdated")}: {data?.data.updated_at}
                  </Typography>
                )}
              </Box>
            </Stack>
            {/* divider */}
            <Box bgcolor={"#F2F4F7"} width={"100%"} />

            <Stack spacing={1} direction={"row"} mb={3} alignItems={"center"}>
              <Typography
                color={"#475467"}
                lineHeight={"19.2px"}
                onClick={navigateToAggregators}
                sx={{ cursor: "pointer" }}
              >
                {t("aggs")}
              </Typography>
              <img src={RightArrow} alt="" />
              <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
                <Typography
                  color={"#344054"}
                  fontWeight={600}
                  lineHeight={"19.2px"}
                >
                  {mode === "add" ? t("addNew") : t("edit")} {t("agg")}
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={2} width={{ xs: "100%", md: "30%" }} mb={2}>
              {/* Aggregator Name  */}
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t("aggName")}
                    {...field}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: field.value !== undefined,
                    }}
                  />
                )}
              />
              {/* Related payment Select  */}
              <FormControl
                fullWidth
                size="small"
                error={!!errors.payment_methods}
              >
                <InputLabel id="demo-multiple-checkbox-label">
                  {t("selectPayments")}
                </InputLabel>
                <Controller
                  name={`payment_methods`}
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      input={<OutlinedInput label={t("selectPayments")} />}
                      renderValue={(selected) =>
                        selected
                          .map(
                            (selectedId) =>
                              paymentsData?.data.find(
                                (payment) => payment.id === +selectedId,
                              )?.name,
                          )
                          .join(", ")
                      }
                    >
                      {paymentsData?.data
                        .filter(
                          (pay) =>
                            !pay.is_discount &&
                            (!pay.has_aggregator ||
                              (mode === "edit" &&
                                data?.data.related_payment_methods
                                  .map((item: any) => item.payment_method.id)
                                  .includes(pay.id))),
                        )
                        .map((payment) => (
                          <MenuItem
                            key={payment.id}
                            value={payment.id}
                            sx={{ p: 0 }}
                          >
                            <Checkbox
                              size="small"
                              checked={
                                field.value &&
                                field.value.indexOf(payment.id) > -1
                              }
                            />
                            {payment.name}
                          </MenuItem>
                        ))}
                      {paymentsData?.data.filter((pay) => !pay.is_aggregator)
                        .length === 0 &&
                        mode === "add" && (
                          <Typography align="center">
                            {t("noPaysAvailable")}
                          </Typography>
                        )}
                    </Select>
                  )}
                />
                <FormHelperText id="my-helper-text">
                  {errors.payment_methods?.message}
                </FormHelperText>
              </FormControl>
            </Stack>
            {/* agg fees */}
            <Typography fontWeight={500} mb={2}>
              {t("aggFees")}
            </Typography>
            <Stack gap={1}>
              {fields.map((itemField, index) => {
                return (
                  <Box
                    key={itemField.id}
                    display={"flex"}
                    alignItems={"center"}
                    gap={2}
                    // justifyContent={"space-between"}
                    // padding={"14px 16px"}
                    // borderRadius={1.5}
                    // border={"2px solid #F0F0F0"}
                    // boxShadow={"rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"}
                  >
                    <Stack direction={"row"} gap={1}>
                      <Controller
                        name={`aggFeesList.${index}.from`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={`${generalT("from")}`}
                            variant="outlined"
                            {...field}
                            error={!!errors.aggFeesList?.[index]?.from}
                            helperText={
                              errors.aggFeesList?.[index]?.from?.message
                            }
                            fullWidth
                            size="small"
                            sx={{ minWidth: 160, width: 160 }}
                          />
                        )}
                      />
                      <Controller
                        name={`aggFeesList.${index}.to`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={`${generalT("to")}`}
                            variant="outlined"
                            {...field}
                            error={!!errors.aggFeesList?.[index]?.to}
                            helperText={
                              errors.aggFeesList?.[index]?.to?.message
                            }
                            fullWidth
                            size="small"
                            sx={{ minWidth: 160, width: 160 }}
                          />
                        )}
                      />
                      <Controller
                        name={`aggFeesList.${index}.fee`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={`${t("aggFees")}`}
                            variant="outlined"
                            {...field}
                            error={!!errors.aggFeesList?.[index]?.fee}
                            helperText={
                              errors.aggFeesList?.[index]?.fee?.message
                            }
                            fullWidth
                            size="small"
                            sx={{ minWidth: 160, width: 160 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography fontSize={"14px"}>
                                    {watch("aggFeesList")[index].feeType ===
                                    "percentage"
                                      ? "%"
                                      : "SAR"}
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                      <FormControl
                        size="small"
                        error={!!errors.aggFeesList?.[index]?.type}
                      >
                        <InputLabel id="demo-simple-select-label">
                          {generalT("type")}
                        </InputLabel>
                        <Controller
                          name={`aggFeesList.${index}.feeType`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              key={field.value}
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              label={generalT("type")}
                              sx={{ minWidth: 130, width: 130 }}
                            >
                              <MenuItem value={"percentage"}>
                                {t("percentage")}
                              </MenuItem>
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {generalT(
                            `${
                              errors.aggFeesList?.[index]?.feeType?.message ??
                              ""
                            }`,
                          )}
                        </FormHelperText>
                      </FormControl>
                      <FormControl
                        size="small"
                        error={!!errors.aggFeesList?.[index]?.applyOn}
                      >
                        <InputLabel id="demo-multiple-checkbox-label">
                          {t("applyOn")}
                        </InputLabel>
                        <Controller
                          name={`aggFeesList.${index}.applyOn`}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              {...field}
                              size="small"
                              sx={{ minWidth: 170, width: 170 }}
                              labelId="demo-multiple-checkbox-label"
                              id="demo-multiple-checkbox"
                              multiple
                              maxRows={2}
                              input={<OutlinedInput label={t("includeIn")} />}
                              renderValue={(selected) =>
                                selected.map((value) => t(value)).join(", ")
                              }
                            >
                              {methodsOptions.map((item) => (
                                <MenuItem
                                  key={item.value}
                                  value={item.value}
                                  sx={{ p: 0 }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      field.value &&
                                      field.value.indexOf(item.value) > -1
                                    }
                                  />
                                  {t(item.label)}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {errors.aggFeesList?.[index]?.applyOn?.message}
                        </FormHelperText>
                      </FormControl>
                      {/* payments */}
                      <FormControl
                        size="small"
                        error={!!errors.aggFeesList?.[index]?.payment_methods}
                      >
                        <InputLabel id="demo-multiple-checkbox-label">
                          {t("selectPayments")}
                        </InputLabel>
                        <Controller
                          name={`aggFeesList.${index}.payment_methods`}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              sx={{ minWidth: 170, width: 170 }}
                              {...field}
                              labelId="demo-multiple-checkbox-label"
                              id="demo-multiple-checkbox"
                              multiple
                              input={
                                <OutlinedInput label={t("selectPayments")} />
                              }
                              renderValue={(selected) =>
                                selected
                                  .map(
                                    (selectedId) =>
                                      paymentsData?.data.find(
                                        (payment) => payment.id === +selectedId,
                                      )?.name,
                                  )
                                  .join(", ")
                              }
                            >
                              {paymentsData?.data
                                .filter((pay) =>
                                  watch("payment_methods").includes(pay.id),
                                )
                                .map((payment) => (
                                  <MenuItem
                                    key={payment.id}
                                    sx={{ p: 0 }}
                                    value={payment.id}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        field.value &&
                                        field.value.indexOf(payment.id) > -1
                                      }
                                    />
                                    {payment.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {
                            errors.aggFeesList?.[index]?.payment_methods
                              ?.message
                          }
                        </FormHelperText>
                      </FormControl>
                      {/* branches */}
                      <FormControl
                        size="small"
                        error={!!errors.aggFeesList?.[index]?.branches}
                      >
                        <InputLabel id="demo-multiple-checkbox-label">
                          {generalT("selectBranches")}
                        </InputLabel>
                        <Controller
                          name={`aggFeesList.${index}.branches`}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              sx={{ minWidth: 170, width: 170 }}
                              {...field}
                              labelId="demo-multiple-checkbox-label"
                              id="demo-multiple-checkbox"
                              multiple
                              input={
                                <OutlinedInput
                                  label={generalT("selectBranches")}
                                />
                              }
                              renderValue={(selected) =>
                                selected
                                  .map(
                                    (selectedId) =>
                                      branchesData?.data.find(
                                        (payment) => payment.id === +selectedId,
                                      )?.name,
                                  )
                                  .join(", ")
                              }
                            >
                              {branchesData?.data.map((payment) => (
                                <MenuItem
                                  key={payment.id}
                                  sx={{ p: 0 }}
                                  value={payment.id}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      field.value &&
                                      field.value.indexOf(payment.id) > -1
                                    }
                                  />
                                  {payment.name}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {errors.aggFeesList?.[index]?.branches?.message}
                        </FormHelperText>
                      </FormControl>
                    </Stack>
                    <IconButton onClick={() => handleRemoveFee(index)}>
                      <img src={DeleteIcon} alt="" />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
            <FormHelperText id="my-helper-text" error sx={{ mt: -1 }}>
              {errors.aggFeesList?.message}
            </FormHelperText>
            <Stack alignItems={"flex-start"} mt={1}>
              <Button variant="contained" onClick={handleAppendFee}>
                {t("add")}
              </Button>
            </Stack>
            {/* end agg fees */}
            <Typography fontWeight={500} mt={3} mb={2}>
              {t("aggDeliveryCost")}
            </Typography>
            <Stack gap={1}>
              {deliveryFields.map((itemField, index) => {
                return (
                  <Box
                    key={itemField.id}
                    display={"flex"}
                    alignItems={"center"}
                    gap={2}
                  >
                    <Stack direction={"row"} gap={1}>
                      <Controller
                        name={`deliveryCostList.${index}.from`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={`${generalT("from")}`}
                            variant="outlined"
                            {...field}
                            error={!!errors.deliveryCostList?.[index]?.from}
                            helperText={
                              errors.deliveryCostList?.[index]?.from?.message
                            }
                            fullWidth
                            size="small"
                            sx={{ minWidth: 160, width: 160 }}
                          />
                        )}
                      />
                      <Controller
                        name={`deliveryCostList.${index}.to`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={`${generalT("to")}`}
                            variant="outlined"
                            {...field}
                            error={!!errors.deliveryCostList?.[index]?.to}
                            helperText={
                              errors.deliveryCostList?.[index]?.to?.message
                            }
                            fullWidth
                            size="small"
                            sx={{ minWidth: 160, width: 160 }}
                          />
                        )}
                      />
                      <Controller
                        name={`deliveryCostList.${index}.fee`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={`${t("deliveryCostWithTax")}`}
                            variant="outlined"
                            {...field}
                            error={!!errors.deliveryCostList?.[index]?.fee}
                            helperText={
                              errors.deliveryCostList?.[index]?.fee?.message
                            }
                            fullWidth
                            size="small"
                            sx={{ minWidth: 160, width: 160 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography fontSize={"14px"}>
                                    {" "}
                                    {watch("deliveryCostList")[index]
                                      .feeType === "percentage"
                                      ? "%"
                                      : "SAR"}
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                      <FormControl
                        size="small"
                        error={!!errors.deliveryCostList?.[index]?.type}
                      >
                        <InputLabel id="demo-simple-select-label">
                          {generalT("type")}
                        </InputLabel>
                        <Controller
                          name={`deliveryCostList.${index}.feeType`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              key={field.value}
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              label={generalT("type")}
                              sx={{ minWidth: 130, width: 130 }}
                            >
                              <MenuItem value={"percentage"}>
                                {t("percentage")}
                              </MenuItem>
                              <MenuItem value={"amount"}>
                                {t("amount")}
                              </MenuItem>
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {generalT(
                            `${
                              errors.deliveryCostList?.[index]?.feeType
                                ?.message ?? ""
                            }`,
                          )}
                        </FormHelperText>
                      </FormControl>
                      <FormControl
                        size="small"
                        error={!!errors.deliveryCostList?.[index]?.applyOn}
                      >
                        <InputLabel id="demo-multiple-checkbox-label">
                          {t("applyOn")}
                        </InputLabel>
                        <Controller
                          name={`deliveryCostList.${index}.applyOn`}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              {...field}
                              size="small"
                              sx={{ minWidth: 170, width: 170 }}
                              labelId="demo-multiple-checkbox-label"
                              id="demo-multiple-checkbox"
                              multiple
                              maxRows={2}
                              input={<OutlinedInput label={t("includeIn")} />}
                              renderValue={(selected) =>
                                selected.map((value) => t(value)).join(", ")
                              }
                            >
                              {methodsOptions.map((item) => (
                                <MenuItem
                                  key={item.value}
                                  value={item.value}
                                  sx={{ p: 0 }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      field.value &&
                                      field.value.indexOf(item.value) > -1
                                    }
                                  />
                                  {t(item.label)}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {errors.deliveryCostList?.[index]?.applyOn?.message}
                        </FormHelperText>
                      </FormControl>
                      {/* payments */}
                      <FormControl
                        size="small"
                        error={
                          !!errors.deliveryCostList?.[index]?.payment_methods
                        }
                      >
                        <InputLabel id="demo-multiple-checkbox-label">
                          {t("selectPayments")}
                        </InputLabel>
                        <Controller
                          name={`deliveryCostList.${index}.payment_methods`}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              sx={{ minWidth: 170, width: 170 }}
                              {...field}
                              labelId="demo-multiple-checkbox-label"
                              id="demo-multiple-checkbox"
                              multiple
                              input={
                                <OutlinedInput label={t("selectPayments")} />
                              }
                              renderValue={(selected) =>
                                selected
                                  .map(
                                    (selectedId) =>
                                      paymentsData?.data.find(
                                        (payment) => payment.id === +selectedId,
                                      )?.name,
                                  )
                                  .join(", ")
                              }
                            >
                              {paymentsData?.data
                                .filter((pay) =>
                                  watch("payment_methods").includes(pay.id),
                                )
                                .map((payment) => (
                                  <MenuItem
                                    key={payment.id}
                                    sx={{ p: 0 }}
                                    value={payment.id}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        field.value &&
                                        field.value.indexOf(payment.id) > -1
                                      }
                                    />
                                    {payment.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {
                            errors.deliveryCostList?.[index]?.payment_methods
                              ?.message
                          }
                        </FormHelperText>
                      </FormControl>
                      {/* branches */}
                      <FormControl
                        size="small"
                        error={!!errors.deliveryCostList?.[index]?.branches}
                      >
                        <InputLabel id="demo-multiple-checkbox-label">
                          {generalT("selectBranches")}
                        </InputLabel>
                        <Controller
                          name={`deliveryCostList.${index}.branches`}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              sx={{ minWidth: 170, width: 170 }}
                              {...field}
                              labelId="demo-multiple-checkbox-label"
                              id="demo-multiple-checkbox"
                              multiple
                              input={
                                <OutlinedInput
                                  label={generalT("selectBranches")}
                                />
                              }
                              renderValue={(selected) =>
                                selected
                                  .map(
                                    (selectedId) =>
                                      branchesData?.data.find(
                                        (payment) => payment.id === +selectedId,
                                      )?.name,
                                  )
                                  .join(", ")
                              }
                            >
                              {branchesData?.data.map((payment) => (
                                <MenuItem
                                  key={payment.id}
                                  sx={{ p: 0 }}
                                  value={payment.id}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      field.value &&
                                      field.value.indexOf(payment.id) > -1
                                    }
                                  />
                                  {payment.name}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {errors.deliveryCostList?.[index]?.branches?.message}
                        </FormHelperText>
                      </FormControl>
                    </Stack>
                    <IconButton onClick={() => handleRemoveDeliveryCost(index)}>
                      <img src={DeleteIcon} alt="" />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
            <Stack alignItems={"flex-start"} mt={1}>
              <Button variant="contained" onClick={handleAppendDeliveryCost}>
                {t("add")}
              </Button>
            </Stack>
          </Box>

          <Box
            p={{ xs: " 24px 16px", sm: 4 }}
            bgcolor={"#FCFCFD"}
            display={"flex"}
            justifyContent={"center"}
            boxShadow={"0 -8px 50px -5px rgba(50, 50, 50, 0.1)"}
          >
            <Stack
              direction={"row"}
              sx={{ minWidth: { xs: "100%", md: 872 } }}
              justifyContent={"space-between"}
            >
              <Button
                variant="outlined"
                color="tertiary"
                onClick={navigateToAggregators}
              >
                {t("cancel")}
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                endIcon={<KeyboardArrowRightIcon />}
                loading={isPending || isPendingUpdate}
              >
                {t("submit")}
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      </form>
    </Stack>
  );
};

export default AddNewAggregator;
